import { useRef, useState } from 'react';
import { buildExport, dataUrlToBlob, parseImport } from '../../workspace/exportImport.js';
import { emptyWorkspace } from '../../workspace/schema.js';
import { clearEverything, getDocBlob, saveDocBlob } from '../../workspace/store.js';
import { PageHead } from '../components.js';
import { useWorkspace } from '../state.js';

export function Privacy() {
  const { ws, replace, persistent } = useWorkspace();
  const [msg, setMsg] = useState<{ kind: 'good' | 'bad'; text: string } | null>(null);
  const file = useRef<HTMLInputElement>(null);

  const doExport = async () => {
    const data = await buildExport(ws, getDocBlob);
    const url = URL.createObjectURL(new Blob([JSON.stringify(data)], { type: 'application/json' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `tyche-workspace-${new Date().toISOString().slice(0, 10)}.tyche.json`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5_000);
    setMsg({ kind: 'good', text: 'Saved a copy of your workspace, including your documents, to your downloads.' });
  };

  const doImport = async (f: File | undefined) => {
    if (!f) return;
    try {
      const { workspace, files } = parseImport(await f.text());
      if (!window.confirm('Replace what is in this browser with the imported workspace?')) return;
      await clearEverything();
      for (const [id, url] of Object.entries(files)) await saveDocBlob(id, await dataUrlToBlob(url));
      replace(workspace);
      setMsg({ kind: 'good', text: 'Workspace imported.' });
    } catch (e) {
      setMsg({ kind: 'bad', text: e instanceof Error ? e.message : 'That file could not be imported.' });
    } finally {
      if (file.current) file.current.value = '';
    }
  };

  const doDelete = async () => {
    if (!window.confirm('Delete everything this app has stored in this browser — answers, figures, documents and scenarios? This cannot be undone.')) return;
    await clearEverything();
    replace(emptyWorkspace());
    setMsg({ kind: 'good', text: 'Everything has been deleted from this browser.' });
  };

  return (
    <>
      <PageHead eyebrow="Your data" title="Your data and privacy">
        Everything stays on this device. Here is exactly what is kept and how to move or delete it.
      </PageHead>
      <div className="stack-lg">
        {msg && <p className={`callout ${msg.kind}`} role="status">{msg.text}</p>}
        <section className="card">
          <h2>What is stored, and where</h2>
          <ul>
            <li>Your answers, figures, scenarios and checklist — in this browser&rsquo;s own storage{persistent ? '' : ' (not available right now, so nothing is being kept)'}.</li>
            <li>Documents you upload — in this browser too, so you can reopen the page a figure came from. They are read here and never uploaded.</li>
            <li>Your documents and figures are never sent to a server. The one exception is the optional loan advisor chat: when you send it a message, that message and your calculator results (not your files) go to Cloudflare Workers AI to write a reply. Tyche does not store them. There are no accounts, no tracking and no analytics.</li>
          </ul>
          <p className="small muted">
            Clearing your browser data deletes your workspace. Use export to keep a copy or to move to another device.
          </p>
        </section>
        <section className="card">
          <h2>Move or back up your workspace</h2>
          <div className="row">
            <button className="btn primary" type="button" onClick={() => void doExport()}>Export workspace</button>
            <button className="btn" type="button" onClick={() => file.current?.click()}>Import a workspace</button>
            <input ref={file} type="file" accept=".json,application/json" hidden onChange={(e) => void doImport(e.target.files?.[0])} />
          </div>
          <p className="small muted" style={{ marginTop: 10 }}>The export file contains your documents and figures — store it somewhere private.</p>
        </section>
        <section className="card">
          <h2>Delete everything</h2>
          <p className="muted">Removes every answer, figure, document and scenario from this browser.</p>
          <button className="btn danger" type="button" onClick={() => void doDelete()}>Delete everything</button>
        </section>
      </div>
    </>
  );
}
